import {
  Box,
  Divider,
  FormControl,
  Input,
  MenuItem,
  Modal,
  Select,
  Typography,
} from "@mui/material";
import { useEffect, useState } from "react";
import useAPI from "../../../../api/hooks/useAPI";
import useColors from "../../../../api/hooks/useColors";
import PrimaryButton from "../../../../components/buttons/PrimaryButton";
import SecondaryButton from "../../../../components/buttons/SecondaryButton";
import LoadingIcon from "../../../../components/LoadingIcon";
import { Toast } from "../../../../components/Toast";
import { UserPlatformAccessRole } from "../../../../utils/constants";
import { customerTimezone, defaultTimezone } from "../../constants";
import { createTimezoneTitle } from "../../notifications/pages/GeneralNotifications/FrequencyNotificationsSection/TimezoneSelector";

const AddNewUserModal = ({ open, handleClose }) => {
  const colors = useColors();
  const { trackEvent, getCustomerList, createUserWithNotifications } = useAPI();

  const [userDetails, setUserDetails] = useState({
    customerId: "",
    email: "",
    displayName: "",
    accessRole: UserPlatformAccessRole.VIEWER,
    timezone: defaultTimezone,
  });

  const [customerList, setCustomerList] = useState([]);
  const [onSave, setOnSave] = useState(false);
  const [isAllPropertiesFilled, setIsAllPropertiesFilled] = useState(false);
  const [emailError, setEmailError] = useState("");

  useEffect(() => {
    if (open) {
      getCustomerList()
        .then((response) => {
          setCustomerList(response.data);
        })
        .catch((error) => {
          Toast.error("Failed to fetch customer list");
        });
    }
  }, [open]);

  const setAllProperties = () => {
    setUserDetails({
      customerId: "",
      email: "",
      displayName: "",
      accessRole: UserPlatformAccessRole.VIEWER,
    });
    setIsAllPropertiesFilled(false);
  };

  const handleSave = () => {
    // No need to check the domain anymore since we auto-append it
    setOnSave(true);
    createUserWithNotifications({
      customer_id: userDetails.customerId,
      email: userDetails.email,
      display_name: userDetails.displayName,
      access_role: userDetails.accessRole,
      timezone: userDetails.timezone,
    })
      .then((response) => {
        trackEvent(`Added new user ${userDetails.email}`);
        Toast.success("User added successfully");
        handleClose();
      })
      .catch((error) => {
        trackEvent(`Failed to add user ${userDetails.email}`);
        if (error.response) {
          Toast.error(error.response.data);
        } else {
          Toast.error("Failed to add user");
        }
      })
      .finally(() => {
        setOnSave(false);
      });
  };

  const handleCancel = () => {
    trackEvent("User cancelled the add new user dialog");
    setAllProperties();
    handleClose();
  };

  useEffect(() => {
    if (
      userDetails.customerId &&
      userDetails.email &&
      userDetails.displayName &&
      userDetails.accessRole &&
      !emailError
    ) {
      setIsAllPropertiesFilled(true);
    } else {
      setIsAllPropertiesFilled(false);
    }
  }, [userDetails, emailError]);

  // Set email automatically if customerId changes and starts with "dev."
  useEffect(() => {
    if (userDetails.customerId && userDetails.customerId.startsWith("dev.")) {
      const emailPrefix = userDetails.customerId;
      setUserDetails((prevDetails) => ({
        ...prevDetails,
        email: `${emailPrefix}@fake.com`,
      }));
    }
  }, [userDetails.customerId]);

  // Validate email when customerId or email changes
  useEffect(() => {
    // Clear any previous errors
    setEmailError("");

    // If no customerId selected, we can't validate the domain
    if (!userDetails.customerId) {
      return;
    }

    // If no username entered, no validation needed yet
    const username = userDetails.email.split("@")[0];
    if (!username) {
      return;
    }

    // Since we're auto-appending the domain, we just need to make sure the
    // email has a valid format with the username part
    if (!/^[a-zA-Z0-9._%+-]+$/.test(username)) {
      setEmailError("Please enter a valid email username");
    }
  }, [userDetails.customerId, userDetails.email]);

  return (
    <Modal
      open={open}
      onClose={handleClose}
      aria-labelledby="modal-modal-title"
    >
      <Box
        sx={{
          width: 500,
          bgcolor: "background.paper",
          margin: "auto",
          position: "absolute",
          top: "50%",
          left: "50%",
          transform: "translate(-50%, -50%)",
          boxShadow: 24,
          overflowY: "auto",
          borderRadius: "10px",
          overflow: "hidden",
        }}
      >
        <Box
          sx={{
            width: "100%",
            height: "100%",
            overflowY: "auto",
          }}
        >
          <Box
            sx={{
              textAlign: "center",
              position: "sticky",
              top: 0,
              zIndex: 1,
              backgroundColor: colors.common_white,
              pt: "5%",
            }}
            id="modal-modal-title"
          >
            <Typography variant="subtitle1" gutterBottom>
              Add New User
            </Typography>
            <Typography variant="body1" gutterBottom sx={{ marginTop: "4px" }}>
              Enter user details
            </Typography>
          </Box>
          <Box sx={{ padding: "32px" }}>
            {/* Customer ID */}
            <FormControl variant="standard" fullWidth>
              <Typography variant="subtitle2" fontSize={"14px"}>
                Customer ID
              </Typography>
              <Select
                value={userDetails.customerId}
                onChange={(e) =>
                  setUserDetails((prev) => ({
                    ...prev,
                    customerId: e.target.value,
                  }))
                }
                sx={{
                  "& .MuiSelect-select": {
                    fontSize: "12px",
                  },
                }}
              >
                {customerList.map((customerId) => (
                  <MenuItem key={customerId} value={customerId}>
                    {customerId}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <Divider style={{ marginBottom: "16px", marginTop: "0px" }} />

            {/* Email */}
            <FormControl variant="standard" fullWidth>
              <Typography variant="subtitle2" fontSize={"14px"}>
                Email
              </Typography>
              <Box
                sx={{
                  display: "flex",
                  alignItems: "flex-end",
                  position: "relative",
                  marginBottom: "4px",
                }}
              >
                {userDetails.customerId?.startsWith("dev.") ? (
                  <Typography
                    variant="body2"
                    component="span"
                    sx={{
                      fontSize: "12px",
                      color: colors.grey_60percent,
                      paddingBottom: "2px",
                    }}
                  >
                    {userDetails.email || `${userDetails.customerId}@fake.com`}
                  </Typography>
                ) : (
                  <>
                    <Input
                      placeholder="username"
                      value={userDetails.email.split("@")[0]}
                      onChange={(event) => {
                        const username = event.target.value;
                        const domain = userDetails.customerId;
                        const email = domain
                          ? `${username}@${domain}`
                          : username;
                        setUserDetails((prevDetails) => ({
                          ...prevDetails,
                          email: email,
                        }));
                      }}
                      disabled={!userDetails.customerId}
                      disableUnderline
                      sx={{
                        flexGrow: 0,
                        width: "auto",
                        "& .MuiInput-input": {
                          borderBottom: "none",
                          fontSize: "12px",
                          paddingRight: "0px",
                          paddingBottom: "2px",
                        },
                      }}
                    />
                    <Typography
                      variant="body2"
                      component="span"
                      sx={{
                        fontSize: "12px",
                        color: userDetails.customerId
                          ? colors.grey_60percent
                          : "transparent",
                        paddingBottom: "2px",
                        visibility: userDetails.customerId
                          ? "visible"
                          : "hidden",
                      }}
                    >
                      @{userDetails.customerId || "example.com"}
                    </Typography>
                  </>
                )}
                <Box
                  sx={{
                    position: "absolute",
                    bottom: 0,
                    left: 0,
                    right: 0,
                    height: "1px",
                    backgroundColor: emailError
                      ? colors.error_main
                      : colors.grey_32percent,
                    opacity: userDetails.customerId ? 1 : 0.5,
                  }}
                />
              </Box>
              <Typography
                variant="caption"
                sx={{
                  color: emailError ? colors.error_main : colors.grey_60percent,
                  fontSize: "10px",
                  mt: 0.5,
                  display: "block",
                }}
              >
                {emailError
                  ? emailError
                  : userDetails.customerId
                  ? ""
                  : "Please select a customer ID first"}
              </Typography>
            </FormControl>
            <Divider style={{ marginBottom: "16px", marginTop: "0px" }} />

            {/* Full Name */}
            <FormControl variant="standard" fullWidth>
              <Typography variant="subtitle2" fontSize={"14px"}>
                Full Name
              </Typography>
              <Input
                placeholder="name"
                value={userDetails.displayName}
                onChange={(event) =>
                  setUserDetails((prevDetails) => ({
                    ...prevDetails,
                    displayName: event.target.value,
                  }))
                }
                disableUnderline
                sx={{
                  "& .MuiInput-input": {
                    borderBottom: `1px solid ${colors.grey_32percent}`,
                    fontSize: "12px",
                  },
                }}
              />
            </FormControl>
            <Divider style={{ marginBottom: "16px", marginTop: "0px" }} />

            {/* User Type */}
            <FormControl variant="standard" fullWidth>
              <Typography variant="subtitle2" fontSize={"14px"}>
                User Type
              </Typography>
              <Select
                value={userDetails.accessRole}
                onChange={(e) =>
                  setUserDetails((prev) => ({
                    ...prev,
                    accessRole: e.target.value,
                  }))
                }
                sx={{
                  "& .MuiSelect-select": {
                    fontSize: "12px",
                  },
                }}
              >
                {Object.entries(UserPlatformAccessRole).map(
                  ([roleKey, roleValue]) => (
                    <MenuItem key={roleKey} value={roleValue}>
                      {roleValue}
                    </MenuItem>
                  )
                )}
              </Select>
            </FormControl>
            <Divider style={{ marginBottom: "16px", marginTop: "0px" }} />

            {/* Timezone */}
            <FormControl variant="standard" fullWidth>
              <Typography variant="subtitle2" fontSize={"14px"}>
                Timezone
              </Typography>
              <Select
                value={userDetails.timezone}
                onChange={(e) =>
                  setUserDetails((prev) => ({
                    ...prev,
                    timezone: e.target.value,
                  }))
                }
                sx={{
                  "& .MuiSelect-select": {
                    fontSize: "12px",
                  },
                }}
              >
                {Object.keys(customerTimezone).map((ianaTimezone) => (
                  <MenuItem key={ianaTimezone} value={ianaTimezone}>
                    {createTimezoneTitle(ianaTimezone)}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Box>
          <Box
            sx={{
              display: "flex",
              justifyContent: "center",
              gap: 2,
              alignItems: "center",
              marginBottom: "24px",
            }}
          >
            <SecondaryButton
              onClick={handleCancel}
              sx={{
                color: colors.primary_main,
                borderRadius: "10px",
                border: "none",
                height: "30px",
                fontSize: "12px",
              }}
            >
              Cancel
            </SecondaryButton>
            {onSave ? (
              <LoadingIcon />
            ) : (
              <PrimaryButton
                onClick={handleSave}
                disabled={!isAllPropertiesFilled || onSave}
                style={{
                  color: colors.common_white,
                  backgroundColor:
                    isAllPropertiesFilled && !onSave
                      ? colors.primary_main
                      : colors.primary_light,
                  borderRadius: "10px",
                  border: "none",
                  cursor:
                    isAllPropertiesFilled && !onSave
                      ? "pointer"
                      : "not-allowed",
                }}
                size="small"
              >
                Add User
              </PrimaryButton>
            )}
          </Box>
        </Box>
      </Box>
    </Modal>
  );
};

export default AddNewUserModal;
